import React, { BaseSyntheticEvent } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors/patientDemographicsOptions.selector'
import { savePatientDemographics, setBarriersToCare, setBarriersToCarePNNV } from '../store/patientDemographics.actions'
import { getPatientDemographicsSelector } from '../selectors'
import { IOption, IOptions, IPNNVOption } from '../../../common/common.types'
import { Virtuoso } from 'react-virtuoso'
import { chevronBackOutline } from 'ionicons/icons'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { PatientInformationConstants } from '../../patientInformation/store/patientInformation.constants'
import { setSelectedOption, enableScroll } from '../../patientInformation/store/patientInformation.actions'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../vitals/store/vitals.actions'
import './BarriersToCare.component.css'

const BarriersToCareContent: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const dispatch = useDispatch()
  const { isTablet: isTabletDevice } = useDeviceMetrics()

  const { barriersToCare, barriersToCarePNNV } = patientDemographics
  const { barriersToCareOptions } = patientDemographicsOptions
  const barriersToCarePNNVOptions = barriersToCareOptions.filter((item) => item.isNV || item.isPN)
  const barriersToCareNormalOptions = barriersToCareOptions.filter((item) => !item.isNV && !item.isPN)

  const onSelect = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    const updatedBarriersToCare: IOptions = [...barriersToCare]
    if (e.target.checked) {
      updatedBarriersToCare.push(option)
      dispatch(setBarriersToCarePNNV(null))
    } else {
      const index = updatedBarriersToCare.findIndex((opt) => opt.id === option.id)
      if (index !== -1) {
        updatedBarriersToCare.splice(index, 1)
      }
    }
    dispatch(setBarriersToCare(updatedBarriersToCare))
    dispatch(savePatientDemographics())
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTabletDevice) {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION))
      history.push(TabConstants.PATIENT_INFO_ROUTE)
    } else {
      history.push(TabConstants.PATIENT_DEMOGRAPHICS)
    }
    dispatch(enableScroll(true))
  }

  const togglePNNVHandler = (isChecked: boolean, option: IOption) => {
    if (barriersToCare && barriersToCare.length > 0 && isChecked) {
      dispatch(setBarriersToCarePNNV(null))
      showConfirmationBeforeClearingRegularValues(option)
    } else {
      togglePNNVOption(isChecked, option)
    }
  }

  const showConfirmationBeforeClearingRegularValues = (option: IOption) => {
    const alertState: IAlertState = {
      header: t('patientDemographics.details.BarriersToCare'),
      message: t('pnnv.userConfirmationMessage'),
      buttons: [
        { text: t('pnnv.cancel') },
        { text: t('pnnv.continueAndClear'), role: 'destructive', handler: () => togglePNNVOption(true, option) },
      ],
      showEqualWidthButton: true,
    }
    dispatch(showAlert(alertState))
  }

  const togglePNNVOption = (isChecked: boolean, option: IOption) => {
    if (barriersToCare && barriersToCare.length > 0) {
      dispatch(setBarriersToCare([]))
    }

    if (isChecked) {
      if (option.name) {
        const obj = {
          id: option.id,
          name: option.name,
          pn: '',
          nv: '',
        }
        if (option.nemsisCode) {
          if (option.isPN) {
            obj.pn = String(option.nemsisCode)
          } else if (option.isNV) {
            obj.nv = String(option.nemsisCode)
          }
        }
        dispatch(setBarriersToCarePNNV(obj as IPNNVOption))
      }
    } else {
      dispatch(setBarriersToCarePNNV(null))
    }
    dispatch(savePatientDemographics())
  }

  return (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('patientDemographics.details.BarriersToCare')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent id='barriersToCare' color='light' fullscreen>
        <IonCard className={isTabletDevice ? 'card-width' : 'card-margin'}>
          {barriersToCareNormalOptions?.length > 0 && (
            <Virtuoso
              data={barriersToCareNormalOptions}
              increaseViewportBy={{ top: 2000, bottom: 2000 }}
              style={{
                height: isTabletDevice ? window.innerHeight - 350 : window.innerHeight - 310,
                marginTop: '10px',
              }}
              overscan={10}
              itemContent={(index, option: IOption) => {
                return (
                  <div style={{ minHeight: '44px' }} className='webkit-line-clamp-2'>
                    <IonItem key={`Option_${option.id}`} lines='none' data-skip-auto-enhance>
                      <IonLabel>{option.name}</IonLabel>
                      <IonToggle
                        slot='start'
                        name={option.name}
                        value={`${option.id}`}
                        checked={barriersToCare.filter((o) => o.id === option.id).length > 0}
                        onClick={(e) => onSelect(e, option)}
                        color={'zoll-blue-secondary'}
                      />
                    </IonItem>
                  </div>
                )
              }}
            />
          )}
          {barriersToCarePNNVOptions && barriersToCarePNNVOptions.length > 0 && (
            <div className='barriers-to-care-pnnv-selection-section'>
              <hr className='picklist-divider' />
              <IonList lines='full'>
                {barriersToCarePNNVOptions?.map((option: IOption) => (
                  <IonItem
                    key={'PNNVOption_' + option.id}
                    data-testid={`PNNVOption_${option.id}`}
                    className={`${barriersToCarePNNV?.id === option.id ? 'selected-pnnv-option' : ''} pnnv-option`}>
                    <IonLabel id={`PNNVoptionLabel_${option.id}`}>{option.name}</IonLabel>
                    <IonToggle
                      slot='start'
                      name={option.name}
                      value={`${option.id}`}
                      checked={barriersToCarePNNV?.id === option.id}
                      onClick={(e: BaseSyntheticEvent) => togglePNNVHandler(e.target.checked, option)}
                      disabled={!!barriersToCarePNNV && barriersToCarePNNV?.id !== option.id}
                    />
                  </IonItem>
                ))}
              </IonList>
            </div>
          )}
        </IonCard>
      </IonContent>
    </>
  )
}

export default BarriersToCareContent
